import React from 'react';
import { Grid } from '@mui/material';
import { getInputValue } from '../../../../../common/react';
import {
  isValidDiscordInvitationCode,
  isValidDisplayName,
  isValidFacebookUsername,
  isValidInstagramUsername,
  isValidTiktokUsername,
  isValidTwitterUsername,
  isValidYouTubeChannelName,
} from '../../../../../common/validations';
import { DefaultInput } from '../../../../../common/Styleguide/Common/Input';
import TikTok from '../../../../../common/icons/TikTok';
import Discord from '../../../../../common/icons/Discord';
import Instagram from '../../../../../common/icons/Instagram';
import Facebook from '../../../../../common/icons/Facebook';
import { PublicProfileData } from '../Profile';
import ProfileIcon from '../../../../../common/icons/ProfileIcon';
import YouTube from '../../../../../common/icons/YouTube';
import X from '../../../../../common/icons/X';

export interface SocialProps {
  publicProfileData: PublicProfileData;
  updatePublicProfileDataFn: (data: PublicProfileData) => void;
}

const extractDiscordInviteCode = (input: string): string => {
  // Matches cases like: discord.gg/code, https://discordapp.com/invite/code, https://www.discord.com/invite/code, etc.
  const regex = /(?:https?:\/\/)?(?:www\.)?(?:discord\.gg|discordapp\.com\/invite|discord.com\/invite)\/([a-zA-Z0-9-]+)/i;
  const match = input.match(regex);
  if (match && match[1]) {
    return match[1];
  }

  return input;
};

const Social: React.FC<SocialProps> = ({ publicProfileData, updatePublicProfileDataFn }) => {
  const { displayName, tiktok, discordInvite, instagram, facebook, twitter, youtube } = publicProfileData;

  return (
    <>
      <Grid container columnSpacing={2} rowSpacing={2}>
        <Grid item xs={6}>
          <DefaultInput
            value={displayName}
            adormentIcon={<ProfileIcon />}
            onChange={(ev) => {
              updatePublicProfileDataFn({ ...publicProfileData, displayName: getInputValue(ev), displayNameError: null });
            }}
            header="Display name"
            headerTooltip="Your public developer name, displayed on the game page"
            error={publicProfileData.displayNameError !== null}
            errorText={publicProfileData.displayNameError ? publicProfileData.displayNameError : 'Invalid display name'}
            onBlur={() => {
              const validationResult = isValidDisplayName(publicProfileData.displayName || '');
              if (!validationResult.isValid) {
                updatePublicProfileDataFn({
                  ...publicProfileData,
                  displayNameError: validationResult.error!,
                });
              }
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <DefaultInput
            value={tiktok}
            adormentIcon={<TikTok />}
            placeholder="@crazygames"
            error={publicProfileData.tiktokError !== null}
            errorText={publicProfileData.tiktokError ? publicProfileData.tiktokError : 'Invalid TikTok format'}
            onChange={(ev) => updatePublicProfileDataFn({ ...publicProfileData, tiktok: getInputValue(ev), tiktokError: null })}
            onBlur={() => {
              if (!isValidTiktokUsername(publicProfileData.tiktok || '')) {
                updatePublicProfileDataFn({ ...publicProfileData, tiktokError: 'Invalid TikTok username' });
              }
            }}
            header="TikTok"
          />
        </Grid>
        <Grid item xs={6}>
          <DefaultInput
            value={discordInvite}
            adormentIcon={<Discord />}
            placeholder="crazygames"
            adormentText={{
              text: 'www.discord.gg/',
              width: 122,
            }}
            error={publicProfileData.discordInviteError !== null}
            errorText={publicProfileData.discordInviteError ? publicProfileData.discordInviteError : 'Invalid Discord invite link format'}
            onChange={(ev) => {
              const inputValue = getInputValue(ev);
              const code = extractDiscordInviteCode(inputValue);
              updatePublicProfileDataFn({ ...publicProfileData, discordInvite: code, discordInviteError: null });
            }}
            onBlur={() => {
              if (!isValidDiscordInvitationCode(publicProfileData.discordInvite || '')) {
                updatePublicProfileDataFn({ ...publicProfileData, discordInviteError: 'Invalid Discord invite link' });
              }
            }}
            header="Discord invite link"
          />
        </Grid>
        <Grid item xs={6}>
          <DefaultInput
            value={instagram}
            header="Instagram"
            placeholder="crazygames"
            error={publicProfileData.instagramError !== null}
            errorText={publicProfileData.instagramError ? publicProfileData.instagramError : 'Invalid Instagram format'}
            adormentIcon={<Instagram />}
            adormentText={{
              text: 'www.instagram.com/',
              width: 152,
            }}
            onChange={(ev) => updatePublicProfileDataFn({ ...publicProfileData, instagram: getInputValue(ev), instagramError: null })}
            onBlur={() => {
              if (!isValidInstagramUsername(publicProfileData.instagram || '')) {
                updatePublicProfileDataFn({ ...publicProfileData, instagramError: 'Invalid Instagram username' });
              }
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <DefaultInput
            header="Facebook"
            errorText={publicProfileData.facebookError ? publicProfileData.facebookError : 'Invalid Facebook format'}
            value={facebook}
            placeholder="crazygames"
            error={publicProfileData.facebookError !== null}
            onChange={(ev) => updatePublicProfileDataFn({ ...publicProfileData, facebook: getInputValue(ev), facebookError: null })}
            adormentIcon={<Facebook />}
            adormentText={{
              text: 'www.facebook.com/',
              width: 146,
            }}
            onBlur={() => {
              if (!isValidFacebookUsername(publicProfileData.facebook || '')) {
                updatePublicProfileDataFn({ ...publicProfileData, facebookError: 'Invalid Facebook username' });
              }
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <DefaultInput
            header="X"
            errorText={publicProfileData.twitterError ? publicProfileData.twitterError : 'Invalid X format'}
            placeholder="crazygames"
            value={twitter}
            adormentIcon={<X />}
            adormentText={{
              text: 'www.x.com/',
              width: 89,
            }}
            error={publicProfileData.twitterError !== null}
            onChange={(ev) => updatePublicProfileDataFn({ ...publicProfileData, twitter: getInputValue(ev), twitterError: null })}
            onBlur={() => {
              if (!isValidTwitterUsername(publicProfileData.twitter || '')) {
                updatePublicProfileDataFn({ ...publicProfileData, twitterError: 'Invalid X username' });
              }
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <DefaultInput
            value={youtube}
            adormentIcon={<YouTube />}
            adormentText={{ text: 'www.youtube.com/', width: 137 }}
            placeholder="@crazygames or channel/id"
            error={publicProfileData.youtubeError !== null}
            errorText={publicProfileData.youtubeError ? publicProfileData.youtubeError : 'Invalid YouTube format'}
            onChange={(ev) => updatePublicProfileDataFn({ ...publicProfileData, youtube: getInputValue(ev), youtubeError: null })}
            onBlur={() => {
              if (!isValidYouTubeChannelName(publicProfileData.youtube || '')) {
                updatePublicProfileDataFn({ ...publicProfileData, youtubeError: 'Invalid YouTube link' });
              }
            }}
            header="YouTube"
          />
        </Grid>
      </Grid>
    </>
  );
};

export default Social;
